/**
 * Reading a projection — the SELECT string and the row constructor.
 *
 * Two properties this module exists to guarantee:
 *
 * 1. **The select string and the type cannot disagree.** `METRICS_SELECT` is
 *    derived from `METRICS_KEYS` through `TRADE_COLUMNS`. Nobody hand-types a
 *    column list into a `.select()` call, so the wire shape and the compile-time
 *    shape are the same fact expressed twice from one source.
 *
 * 2. **A projected row is never laundered into a `Trade`.** `metricsRowFromDb`
 *    constructs ONLY the projected keys — no `?? ''`, no `Number(x) || 0`, no
 *    `!!`, no `extras` spread, no `as unknown as Trade`. A key that was not
 *    selected is not on the object, so `undefined` is never manufactured into a
 *    plausible `0`.
 *
 * That second point is the whole difference from `tradeFromDb`, which cannot
 * distinguish an absent column from a NULL one and turns both into a default.
 */
import { METRICS_KEYS, type MetricsRow } from '../../domain/projection';
import { TRADE_COLUMNS } from '../../domain/types';
import { PROJECTION_TAINT } from './hydration';
import type { DbRow } from './serialize';

/**
 * Columns the STALE-TAB GUARD needs, which are not `Trade` fields.
 *
 * ⚠ `updated_at` is not in `TRADE_COLUMNS` — it is not a domain field, it is
 * concurrency metadata — so it cannot be a `TradeColumnKey` and cannot live in
 * `METRICS_KEYS`. It still has to be SELECTED, and forgetting it is silent:
 * `buildUpdatedAtMaps` skips any row without one (`guard.ts:41`), so the baseline
 * comes back an EMPTY MAP, `partitionByStaleness` then reads `s == null` as
 * "no baseline, therefore fresh" (`diff.ts:145`), and the two-tab guard is 100%
 * inert with nothing anywhere reporting it.
 *
 * That is the D40 lesson repeating: removing a column from a prefetch left every
 * test green while disabling the entire feature. Hence a separate named constant,
 * folded into the select by construction, and a test that asserts the baseline is
 * NON-EMPTY rather than merely that it was built.
 */
export const GUARD_COLUMNS: readonly string[] = ['updated_at'];

/**
 * The columns a DOMAIN function reads. Each one earns its place by the gate's
 * negative control: drop it and a number must change.
 */
export const DOMAIN_COLUMNS: readonly string[] = METRICS_KEYS.map((k) => TRADE_COLUMNS[k]);

/**
 * Every column the select asks for.
 *
 * ⚠ Two sets with two DIFFERENT justifications, which is why they are named
 * separately rather than merged. A domain column is justified by "dropping it
 * changes a number". A guard column is justified by "the stale-tab baseline is
 * non-empty" — dropping `updated_at` changes no number at all, which is exactly
 * why it needs its own test and why the numbers gate cannot certify it.
 */
export const METRICS_COLUMNS: readonly string[] = [...DOMAIN_COLUMNS, ...GUARD_COLUMNS];

/** `id,account_id,…,updated_at` — for `sb.from('trades').select(METRICS_SELECT)`. */
export const METRICS_SELECT: string = METRICS_COLUMNS.join(',');

/**
 * Refuse a row that is missing a projected column.
 *
 * Own-property, not null — same reason as `assertFullTradeRow`. A column the
 * server did not return (RLS mask, a typo in the select, a stale Edge Function)
 * arrives as `undefined`, and every downstream reader coerces `undefined` to a
 * plausible number. `net_pnl` absent means every trade reads as a $0 scratch and
 * the whole account reports zero — internally consistent, so nothing looks wrong.
 */
export function assertMetricsRow(r: DbRow): void {
  const absent = METRICS_COLUMNS.filter((c) => !Object.prototype.hasOwnProperty.call(r, c));
  if (absent.length) {
    throw new Error(
      `metricsRowFromDb: row ${String(r['id'] ?? '(no id)')} is missing ${absent.join(', ')}. ` +
        `The projection asked for ${METRICS_COLUMNS.length} columns and the server returned ` +
        `fewer. Every reader coerces an absent column to 0 / null, so this would produce a ` +
        `complete-looking set of wrong numbers rather than an error.`,
    );
  }
}

/**
 * A projected row → the metrics shape.
 *
 * ⚠ The value transforms MIRROR `tradeFromDb` exactly, and that is the point of
 * the gate: `qty` / `points` / `netPnl` / `riskPoints` use `Number(x) || 0`,
 * `rrRealized` preserves null. Those choices are not interchangeable —
 * `rrRealized: 0` would be averaged into every R statistic, while null is
 * skipped. Diverging here would make the narrow path quietly disagree with the
 * full one, which is the single failure this whole exercise must not introduce.
 */
export function metricsRowFromDb(r: DbRow): MetricsRow {
  assertMetricsRow(r);
  const n0 = (v: unknown): number => Number(v) || 0;
  return {
    // ⚠ The taint. It must be ENUMERABLE and OWN so that it travels through a
    // spread — `{ ...BLANK_TRADE, ...metricsRow }` carries it, and `tradeToDb`
    // refuses the result. The id alone cannot do this job: a projected row holds
    // the row's REAL id, so an already-hydrated trade would pass the ledger.
    [PROJECTION_TAINT]: 'metrics',
    id: r['id'],
    accountId: r['account_id'] ?? null,
    date: r['date'] ?? null,
    sessionDate: r['session_date'] ?? null,
    timeIn: r['time_in'] ?? null,
    contract: r['contract'] ?? null,
    qty: n0(r['qty']),
    points: n0(r['points']),
    netPnl: n0(r['net_pnl']),
    riskPoints: n0(r['risk_points']),
    rrRealized: r['rr_realized'] != null ? Number(r['rr_realized']) : null,
    // `unknown` first: the taint key is deliberately NOT part of MetricsRow — it
    // is a runtime marker, and putting it in the type would invite code to read
    // or strip it.
  } as unknown as MetricsRow;
}
